import ApiCollection                   from '@widesk-core/models/ApiCollection';
import ImportModel                     from '@models/map/ImportModel';
import ImportTypeModel                 from '@models/map/ImportTypeModel';
import PlanAnalysisModel               from '@models/map/planImport/PlanAnalysisModel';
import PlanAnalysisSourceModel         from '@models/map/planImport/PlanAnalysisSourceModel';
import PlanAnalysisDetectionsModel     from '@models/map/planImport/PlanAnalysisDetectionsModel';
import PlanAnalysisMappingModel        from '@models/map/planImport/PlanAnalysisMappingModel';
import PlanAnalysisMatchPreviewModel   from '@models/map/planImport/PlanAnalysisMatchPreviewModel';
import PlanAnalysisPreviewModel        from '@models/map/planImport/PlanAnalysisPreviewModel';
import PlanAnalysisGenerateModel       from '@models/map/planImport/PlanAnalysisGenerateModel';
import { planImportTypeReference }     from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';
import type { DxfPlacement }           from '@models/map/dxfTypes';
import type { DxfSuggestedCategory }   from '@models/map/dxfTypes';
import type { PlanDecorLayer }         from '@models/map/dxfTypes';
import type { PlanMatchPreviewResult } from '@models/map/dxfTypes';
import type { PlanRegion }             from '@models/map/dxfTypes';
import type { PlanSourceResult }       from '@models/map/dxfTypes';
import type { PlanGenerateReport }     from '@/TokamapEditor/planImport/planImportTypes';
import type { PlanPreviewResult }      from '@/TokamapEditor/planImport/planImportTypes';

export { PLAN_FILE_ACCEPT } from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';

const JSON_OUT = { Accept: 'application/json' };

export async function createPlanAnalysis(params: {
	file: File;
	mapUrn: Urn;
	projectUrn: Urn;
}): Promise<number> {
	const typeReference = planImportTypeReference(params.file);

	const importTypes = new ApiCollection(ImportTypeModel);
	await importTypes.setFilter('reference', typeReference).list();
	const importType = importTypes.first();
	if (!importType?.urn) throw new Error(`Type d’import « ${typeReference} » introuvable.`);

	const created = new ImportModel();
	await created.patchFormData({
		file: params.file,
		project: params.projectUrn,
		importType: importType.urn,
		importInformations: JSON.stringify({ mapUrn: params.mapUrn }),
	});
	if (!created.id) throw new Error('L’import n’a pas pu être créé.');

	const analyses = new ApiCollection(PlanAnalysisModel);
	await analyses.setFilter('import', created.id).list();
	const analysis = analyses.first();
	if (!analysis?.id) throw new Error('L’analyse du plan est introuvable.');

	return Number(analysis.id);
}

export async function fetchPlanSource(analysisId: number): Promise<PlanSourceResult> {
	const model = new PlanAnalysisSourceModel();
	await model.setId(analysisId).save({ method: 'post', patchAttributes: {}, headers: JSON_OUT });

	return {
		imageDataUri: model.get('imageDataUri', ''),
		width: model.get('width', 0),
		height: model.get('height', 0),
		analysisRegion: model.get('analysisRegion', null),
	};
}

export async function launchDetection(analysisId: number, region: PlanRegion | null): Promise<number> {
	const model = new PlanAnalysisDetectionsModel();
	await model.setId(analysisId).save({
		method: 'post',
		patchAttributes: { analysisRegion: region ?? {} },
		headers: JSON_OUT,
	});

	return model.get('planDetectionId', 0);
}

export async function saveMapping(analysisId: number, payload: {
	layerMapping: DxfSuggestedCategory[];
	categoryPlaceTypeMapping: Record<string, string>;
	matchFieldMapping: Record<string, string>;
	decorLayers: PlanDecorLayer[];
}): Promise<void> {
	const model = new PlanAnalysisMappingModel();
	await model.setId(analysisId).save({ method: 'post', patchAttributes: payload, headers: JSON_OUT });
}

export async function fetchMatchPreview(
	analysisId: number,
	categoryPlaceTypeMapping: Record<string, string>,
): Promise<PlanMatchPreviewResult> {
	const model = new PlanAnalysisMatchPreviewModel();
	await model.setId(analysisId).save({
		method: 'post',
		patchAttributes: { categoryPlaceTypeMapping },
		headers: JSON_OUT,
	});

	return {
		candidates: model.get('candidates', []),
		numbers: model.get('numbers', 0),
	};
}

export async function fetchPlanPreview(analysisId: number): Promise<PlanPreviewResult> {
	const model = new PlanAnalysisPreviewModel();
	await model.setId(analysisId).save({ method: 'post', patchAttributes: {}, headers: JSON_OUT });

	return {
		svgDataUri: model.get('svgDataUri', ''),
		width: model.get('width', 0),
		height: model.get('height', 0),
		pois: model.get('pois', []),
		isGeoreferenced: model.get('isGeoreferenced', false),
		suggestedPlacement: model.get('suggestedPlacement', null),
	};
}

export async function generatePlan(analysisId: number, placement: DxfPlacement): Promise<PlanGenerateReport> {
	const model = new PlanAnalysisGenerateModel();
	await model.setId(analysisId).save({
		method: 'post',
		patchAttributes: { placement, excludedHandles: [] },
	});

	return {
		nbInsertions: model.get('nbInsertions', 0),
		nbUpdates: model.get('nbUpdates', 0),
		nbDuplicates: model.get('nbDuplicates', 0),
		nbLocked: model.get('nbLocked', 0),
		duplicates: model.get('duplicates', []),
	};
}

export function apiErrorMessage(error: unknown, fallback: string): string {
	const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

	return detail || (error as Error)?.message || fallback;
}
